import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { formatCalendarDate, formatTimeOfDay } from '../../utils/dateTime';

interface OrderData {
  orderNumber?: string;
  deliveryMethod?: 'pickup' | 'delivery';
  deliveryFee?: number;
  foodParcel: {
    id: number;
    name: string;
    price: number;
  };
  orderConfiguration?: {
    configuration: {
      bundle_selections: (number | null)[];
      bread_selection: number | null;
      grocery_selections: (number | null)[];
      parcel_item_selections: Record<number, boolean>;
    };
    totalCalculation: {
      total: number;
      breakdown: {
        food_parcel: { name: string; price: number };
        bundles?: { total: number; count: number };
        bread?: { name: string; price: number };
        grocery?: { total: number; items: Array<{ name: string; price: number }> };
        parcel_items?: { total: number; items: Array<{ name: string; price: number }> };
      };
    };
  };
  assignedParcelDay: {
    id: number;
    date: string;
    weekday: string;
    is_currently_open: boolean;
    status: 'open' | 'upcoming' | 'future';
    status_text: string;
    color_code: 'green' | 'orange' | 'red' | 'blue' | 'grey';
    order_time: any;
    pickup_schedule: any;
    notes: string | null;
  } | null;
  /** Full order from API after submit; includes selected pickup slot (parcel_day_pickup_slot) for confirmation. */
  order?: {
    parcel_day_pickup_slot?: { start_time: string; end_time: string } | null;
  } | null;
  customerDetails: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    suburb: string;
    postcode: string;
    notes: string;
  };
}

interface OrderConfirmationProps {
  orderData: OrderData;
}

const OrderConfirmation: React.FC<OrderConfirmationProps> = ({ orderData }) => {
  const formatDate = (dateString: string) => formatCalendarDate(dateString);
  const formatPickupTime = (time: string) => formatTimeOfDay(time);

  return (
    <Box>
      <Paper sx={{ p: 4, textAlign: 'center', mb: 4 }}>
        <CheckCircleOutlineIcon
          sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
        />
        <Typography variant="h4" gutterBottom>
          Order Confirmed!
        </Typography>
        {orderData.orderNumber && (
          <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
            Order #{orderData.orderNumber}
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary" paragraph>
          Thank you for your order. We have sent a confirmation email to{' '}
          {orderData.customerDetails.email}
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Order Details
        </Typography>
        <List>
          {orderData.orderNumber && (
            <>
              <ListItem>
                <ListItemText
                  primary="Order Number"
                  secondary="Your order reference"
                />
                <Typography variant="body1" color="primary" sx={{ fontWeight: 'bold' }}>
                  #{orderData.orderNumber}
                </Typography>
              </ListItem>
              <Divider />
            </>
          )}
          <ListItem>
            <ListItemText
              primary="Food Parcel"
              secondary={orderData.foodParcel.name}
            />
            <Typography variant="body1">
              ${orderData.orderConfiguration?.totalCalculation ? Number(orderData.orderConfiguration.totalCalculation.breakdown.food_parcel.price).toFixed(2) : Number(orderData.foodParcel.price).toFixed(2)}
            </Typography>
          </ListItem>
          <Divider />

          {/* Bundle Selections */}
          {orderData.orderConfiguration?.configuration.bundle_selections.some(id => id !== null) && (
            <>
              <ListItem>
                <ListItemText
                  primary="Selected Bundles"
                  secondary={`${orderData.orderConfiguration.totalCalculation.breakdown.bundles?.count || 0} bundles selected`}
                />
                <Typography variant="body1">
                  ${Number(orderData.orderConfiguration.totalCalculation.breakdown.bundles?.total).toFixed(2) || '0.00'}
                </Typography>
              </ListItem>
              <Divider />
            </>
          )}

          {/* Bread Selection */}
          {orderData.orderConfiguration?.configuration.bread_selection && orderData.orderConfiguration.totalCalculation.breakdown.bread && (
            <>
              <ListItem>
                <ListItemText
                  primary="Bread Selection"
                  secondary={orderData.orderConfiguration.totalCalculation.breakdown.bread.name}
                />
                <Typography variant="body1">
                  ${Number(orderData.orderConfiguration.totalCalculation.breakdown.bread.price).toFixed(2)}
                </Typography>
              </ListItem>
              <Divider />
            </>
          )}

          {/* Grocery Items */}
          {orderData.orderConfiguration?.configuration.grocery_selections.some(id => id !== null) && orderData.orderConfiguration.totalCalculation.breakdown.grocery && (
            <>
              <ListItem>
                <ListItemText
                  primary="Grocery Items"
                  secondary={`${orderData.orderConfiguration.totalCalculation.breakdown.grocery.items.length} items selected`}
                />
                <Typography variant="body1">
                  ${Number(orderData.orderConfiguration.totalCalculation.breakdown.grocery.total).toFixed(2)}
                </Typography>
              </ListItem>
              <Divider />
            </>
          )}

          {/* Optional Items (parcel_items: Grocery Package, Fruit & Veg Box, etc.) */}
          {orderData.orderConfiguration?.totalCalculation.breakdown.parcel_items && orderData.orderConfiguration.totalCalculation.breakdown.parcel_items.total > 0 && (
            <>
              <ListItem>
                <ListItemText
                  primary="Optional Items"
                  secondary={`${orderData.orderConfiguration.totalCalculation.breakdown.parcel_items.items.length} items selected`}
                />
                <Typography variant="body1">
                  ${Number(orderData.orderConfiguration.totalCalculation.breakdown.parcel_items.total).toFixed(2)}
                </Typography>
              </ListItem>
              <Divider />
            </>
          )}

          {/* Add-ons (products from product list) */}
          {orderData.orderConfiguration?.totalCalculation.breakdown.addon_products && orderData.orderConfiguration.totalCalculation.breakdown.addon_products.items?.length > 0 && (
            <>
              <ListItem>
                <ListItemText
                  primary="Add-ons"
                  secondary={`${orderData.orderConfiguration.totalCalculation.breakdown.addon_products.items.length} product(s) selected`}
                />
                <Typography variant="body1">
                  ${Number(orderData.orderConfiguration.totalCalculation.breakdown.addon_products.total).toFixed(2)}
                </Typography>
              </ListItem>
              <Divider />
            </>
          )}

          <ListItem>
            <ListItemText
              primary="Order Total"
              secondary="Items total"
            />
            <Typography variant="body1">
              ${orderData.orderConfiguration?.totalCalculation ? Number(orderData.orderConfiguration.totalCalculation.total).toFixed(2) : Number(orderData.foodParcel.price).toFixed(2)}
            </Typography>
          </ListItem>
          <Divider />

          <ListItem>
            <ListItemText
              primary={orderData.deliveryMethod === 'delivery' ? 'Delivery Date' : 'Pickup Date'}
              secondary={formatDate(orderData.assignedParcelDay?.date || '')}
            />
          </ListItem>
          {orderData.deliveryMethod === 'delivery' ? (
            <ListItem>
              <ListItemText
                primary="Delivery Time"
                secondary="Between 9:00 AM and 1:00 PM"
              />
            </ListItem>
          ) : (
            <ListItem>
              <ListItemText
                primary="Pickup Time"
                secondary={
                  orderData.order?.parcel_day_pickup_slot
                    ? `${formatPickupTime(orderData.order.parcel_day_pickup_slot.start_time)} – ${formatPickupTime(orderData.order.parcel_day_pickup_slot.end_time)}`
                    : 'No pickup time configured'
                }
              />
            </ListItem>
          )}
          {orderData.deliveryMethod === 'delivery' && orderData.deliveryFee && orderData.deliveryFee > 0 && (
            <ListItem>
              <ListItemText
                primary="Delivery Fee"
                secondary="Based on your postcode"
              />
              <Typography variant="body1">
                ${orderData.deliveryFee.toFixed(2)}
              </Typography>
            </ListItem>
          )}
          <Divider />
          <ListItem>
            <ListItemText
              primary="Total"
              secondary={orderData.deliveryMethod === 'delivery' && orderData.deliveryFee && orderData.deliveryFee > 0 ? 'Order + delivery' : 'Amount to pay'}
            />
            <Typography variant="h6" color="primary">
              $
              {(
                (orderData.orderConfiguration?.totalCalculation ? Number(orderData.orderConfiguration.totalCalculation.total) : Number(orderData.foodParcel?.price) || 0) +
                (orderData.deliveryMethod === 'delivery' && orderData.deliveryFee ? Number(orderData.deliveryFee) : 0)
              ).toFixed(2)}
            </Typography>
          </ListItem>
        </List>
      </Paper>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary" paragraph>
          We will contact you if there are any changes to your order.
        </Typography>
        <Button
          component={RouterLink}
          to="/"
          variant="contained"
          color="primary"
          size="large"
        >
          Return to Home
        </Button>
      </Box>
    </Box>
  );
};

export default OrderConfirmation; 